"use client";

import React, { useEffect, useState } from "react";
import { 
  Box, 
  Fade, 
  CircularProgress, 
  Alert, 
  AlertIcon, 
  Text, 
  Flex,
  Container,
  VStack,
  HStack,
  Badge,
  Button,
  Divider,
  Icon
} from "@chakra-ui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useClient } from "../hooks/useClients";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ClientData as ServiceClientData, clientService } from "../services/clientService";

const ClientDetailsPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [apiMeetings, setApiMeetings] = useState<any[]>([]);
  const [clientProjects, setClientProjects] = useState<string[]>([]);
  const [pendingActionsData, setPendingActionsData] = useState<any[]>([]);
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  
  // Get name and email from URL search params
  const searchParams = new URLSearchParams(location.search);
  const clientName = searchParams.get('name') || '';
  const clientEmail = searchParams.get('email') || '';
  
  // Keep clientId as string to support alphanumeric IDs like "B_6qPrOygz"
  const clientId = params.id || "1"; // Changed from parseInt to keep as string
  
  // Use the client hook
  const { client, loading, error, refetch } = useClient({ 
    clientId, 
    enabled: !!clientId 
  });

  // Load additional client data from API
  const loadAdditionalClientData = async (clientId: string, client: ServiceClientData) => {
    if (!clientId) return;
    
    setIsLoadingAdditionalData(true);
    
    try {
      // Fetch client meetings, projects, and pending actions
      const [meetingsData, projectsData, pendingActionsDataResult] = await Promise.all([
        clientService.getClientMeetings(clientId),
        clientService.getClientProjects(clientId),
        clientService.getClientPendingActions(clientId)
      ]);

      setApiMeetings(meetingsData || []);
      setClientProjects(projectsData || []);
      setPendingActionsData(pendingActionsDataResult || []);
      
    } catch (error) {
      console.error("Error loading additional client details data:", error);
    } finally {
      setIsLoadingAdditionalData(false);
    }
  };
  
  useEffect(() => {
    setIsLoaded(true);
  }, [clientId, clientName, clientEmail]);

  useEffect(() => {
    if (client && clientId) {
      loadAdditionalClientData(clientId, client);
    }
  }, [client, clientId]);
  
  const handleBack = () => {
    navigate('/clients');
  };

  const handleViewFullProfile = () => {
    navigate(`/client-profile/${clientId}?name=${encodeURIComponent(clientName)}&email=${encodeURIComponent(clientEmail)}`);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <CircularProgress isIndeterminate color="purple.500" />
        <Text ml={4}>Loading client details...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px">
          <AlertIcon boxSize="40px" mr={0} />
          <Text fontSize="xl" mt={4} mb={2} fontWeight="bold">
            Error Loading Client Details
          </Text>
          <Text>{error}</Text>
        </Alert>
      </Flex>
    );
  }

  if (!client) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Text fontSize="lg" color="gray.500">
          Client not found
        </Text>
      </Flex>
    );
  }

  const isChannelPartner = client.type === "Channel Partner";
  
  return (
    <Fade in={isLoaded} transition={{ enter: { duration: 0.3 } }}>
      <Box height="100vh" bg="gray.50">
        {/* Header */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.200" py={4}>
          <Container maxW="container.xl">
            <HStack justify="space-between" align="center">
              <HStack spacing={4}>
                <Button
                  leftIcon={<ArrowBackIcon />}
                  variant="ghost"
                  onClick={handleBack}
                  size="sm"
                >
                  Back to Clients
                </Button>
                <Divider orientation="vertical" height="24px" />
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="xl" fontWeight="bold" color="gray.800">
                    {client.name || clientName}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {client.email || clientEmail}
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={3}>
                <Badge
                  px={3}
                  py={1}
                  fontSize="sm"
                  fontWeight="500"
                  bg={client.status === 'Active' ? "#38A169" : "#E53E3E"}
                  color="white"
                  borderRadius="md"
                  textTransform="none"
                >
                  {client.status}
                </Badge>
                <Badge
                  px={3}
                  py={1}
                  fontSize="sm"
                  fontWeight="500"
                  bg={isChannelPartner ? "#805AD5" : "#38A169"}
                  color="white"
                  borderRadius="md"
                  textTransform="none"
                >
                  {client.type}
                </Badge>
                {/* <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={handleViewFullProfile}
                >
                  View Full Profile
                </Button> */}
              </HStack>
            </HStack>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxW="container.xl" py={8}>
          <VStack spacing={6} align="stretch">
            {/* Client Summary Card */}
            <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={6}>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="gray.800">
                Client Summary
              </Text>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Contact Information:</Text>
                  <VStack align="flex-end" spacing={1}>
                    <Text fontSize="sm" fontWeight="500">{client.email}</Text>
                    {client.phone && <Text fontSize="sm" color="gray.500">{client.phone}</Text>}
                  </VStack>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Sales Representative:</Text>
                  <Text fontSize="sm" fontWeight="500">{client.salesPerson || 'Not assigned'}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Client Type:</Text>
                  <Badge
                    px={2}
                    py={1}
                    fontSize="xs"
                    bg={isChannelPartner ? "#805AD5" : "#38A169"}
                    color="white"
                    borderRadius="md"
                  >
                    {client.type}
                  </Badge>
                </HStack>
                {client.budget && (
                  <>
                    <Divider />
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">Budget:</Text>
                      <Text fontSize="sm" fontWeight="500">₹{client.budget.toLocaleString()}</Text>
                    </HStack>
                  </>
                )}
              </VStack>
            </Box>

            {/* Projects & Engagement */}
            <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={6}>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="gray.800">
                Projects & Engagement
              </Text>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Projects Shown:</Text>
                  <HStack>
                    <Text fontSize="sm" color="gray.500">({client.projectsShown})</Text>
                    <Text fontSize="sm" fontWeight="500">{client.projectsList}</Text>
                  </HStack>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Total Meetings:</Text>
                  <Text fontSize="sm" fontWeight="500">{client.totalMeetings}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Last Meeting:</Text>
                  <Text fontSize="sm" fontWeight="500">{client.lastMeeting}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Pending Actions:</Text>
                  {client.isPendingActionHighlighted ? (
                    <Badge
                      px={2}
                      py={1}
                      fontSize="xs"
                      fontWeight="600"
                      bg="rgba(229, 62, 62, 0.1)"
                      color="#C53030"
                      borderRadius="sm"
                    >
                      {client.pendingAction}
                    </Badge>
                  ) : (
                    <Text fontSize="sm" fontWeight="500">{client.pendingAction}</Text>
                  )}
                </HStack>
              </VStack>
            </Box>

            {/* Recent Activity */}
            {(apiMeetings.length > 0 || pendingActionsData.length > 0) && (
              <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={6}>
                <Text fontSize="lg" fontWeight="semibold" mb={4} color="gray.800">
                  Recent Activity
                </Text>
                <VStack spacing={3} align="stretch">
                  {apiMeetings.slice(0, 3).map((meeting, index) => (
                    <Box key={index} p={3} bg="gray.50" borderRadius="md">
                      <HStack justify="space-between">
                        <VStack align="flex-start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium">{meeting.name || 'Meeting'}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {meeting.date ? new Date(meeting.date).toLocaleDateString() : 'No date'}
                          </Text>
                        </VStack>
                        <VStack align="flex-end" spacing={1}>
                          <Text fontSize="xs" color="gray.500">{meeting.duration || 'Duration unknown'}</Text>
                          {meeting.scorePercentage && (
                            <Text fontSize="xs" color="green.500">{meeting.scorePercentage}% score</Text>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                  
                  {pendingActionsData.slice(0, 2).map((action, index) => (
                    <Box key={`action-${index}`} p={3} bg="red.50" borderRadius="md" borderLeft="3px solid" borderColor="red.500">
                      <Text fontSize="sm" fontWeight="medium" color="red.700">
                        {action.text || action.description || 'Pending action'}
                      </Text>
                      {action.dueDate && (
                        <Text fontSize="xs" color="red.500">
                          Due: {new Date(action.dueDate).toLocaleDateString()}
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}

            {isLoadingAdditionalData && (
              <Box textAlign="center" py={4}>
                <CircularProgress isIndeterminate color="purple.500" size="sm" />
                <Text mt={2} fontSize="sm" color="gray.500">Loading additional details...</Text>
              </Box>
            )}
          </VStack>
        </Container>
      </Box>
    </Fade>
  );
};

export default ClientDetailsPage; 